import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Button,
  Table,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CgBlock, CgUnblock } from "react-icons/cg";
import { jwtDecode } from "jwt-decode";
import url from "../../../configs/config";
import Card from "../../../components/Card";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import "react-phone-input-2/lib/style.css";
import FadeLoaderComponent from "../../../components/loader-component";
import SearchInput from "../../../components/search-input";
import PaginationComponent from "../../../components/pagination/pagination-component";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";
import { roleTypes } from "../../../data/roleType";

const colors = [
  {
    status: "active",
    color: "bg-primary",
  },
  {
    status: "in-active",
    color: "bg-warning",
  },
  {
    status: "blocked",
    color: "bg-danger",
  },
  {
    status: "pending",
    color: "bg-info",
  },
];

const AccountList = () => {
  const alertRef = useRef(null);
  const alertRefEdit = useRef(null);
  const navigate = useNavigate();
  const [blockIsSubmit, setBlockIsSubmit] = useState(true);
  const [deleteIsSubmit, setDeleteIsSubmit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loader, setLoader] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [options, setOptions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedData, setSelectedData] = useState();
  const [decodedToken, setDecodedToken] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectRole, setSelectRole] = useState("");
  const token = localStorage.getItem("coyote-token");
  const permissions = useSelector(SettingSelector.set_permissions);
  const hasViewPermission = permissions?.user?.view === true;
  const hasUpdatePermission = permissions?.user?.update === true;
  const hasDeletePermission = permissions?.user?.delete === true;
  const hasCreatePermission = permissions?.user?.create === true;
  const hasStatusPermission = permissions?.user?.status === true;
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getAccounts = async (offset = 0, limit = 10, signal = null) => {
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      setLoader(false);
      setSearchLoading(false);
      return;
    } else {
      if (
        decodedToken?.roles.some(
          (role) =>
            role.role_type === roleTypes.SUPER_ADMIN ||
            role.role_type === roleTypes.ADMIN,
        )
      ) {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
          params: {
            offset,
            limit,
            search:
              searchQuery.trim().length >= 2
                ? searchQuery.trim()
                : searchQuery.trim().length < 1
                  ? null
                  : null,
            role: selectRole === "Select a role..." ? "" : selectRole,
          },
          signal: signal,
        };

        try {
          const response = await axios.get(
            `${url.backendURL}admin/users`,
            config,
          );
          setAccounts(response.data.response.data);
          setTotalEntries(response.data.response.users_count);
          setLoader(false);
          setSearchLoading(false);
        } catch (err) {
          if (axios.isCancel(err)) {
            console.log("Request cancelled:", err.message);
            return;
          }
          if (alertRef?.current) {
            alertRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }

          handleError(err, "accounts");
          setLoader(false);
          setSearchLoading(false);
        }
      }
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    getAccounts((currentPage - 1) * entriesPerPage, entriesPerPage);
    setLoading(false);
  }, [token, decodedToken, currentPage, entriesPerPage]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      if (searchQuery.trim().length >= 2 || searchQuery.trim().length < 1) {
        setLoading(true);
        setCurrentPage(1);
        await getAccounts(0, entriesPerPage, abortController.signal);
        setLoading(false);
      }
    };
    fetchData();

    return () => {
      abortController.abort();
    };
  }, [searchQuery]);

  useEffect(() => {
    const abortController = new AbortController();

    setCurrentPage(1);
    getAccounts(0, entriesPerPage, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [selectRole]);

  const getColorByStatus = (status) => {
    const colorObject = colors.find((color) => color.status === status);
    return colorObject ? colorObject.color : "";
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) =>
              role.role_type === roleTypes.SUPER_ADMIN ||
              role.role_type === roleTypes.ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }
    if (
      decodedToken?.roles.some(
        (role) =>
          role.role_type === roleTypes.SUPER_ADMIN ||
          role.role_type === roleTypes.ADMIN,
      )
    ) {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
          data: {
            email: selectedData.email,
          },
        };
        const response = await axios.delete(
          `${url.backendURL}admin/user`,
          config,
        );

        await getAccounts((currentPage - 1) * entriesPerPage, entriesPerPage);
        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        setAlert({
          message: response && response.data.message,
          type: "success",
          show: true,
          alertFor: "accounts",
        });
        setShowPopover(null);
        setDeleteIsSubmit(true);
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
      } catch (err) {
        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "accounts");
        setShowPopover(null);
        setDeleteIsSubmit(true);
      }
    }
  };

  const handleAccountStatus = async (e, status) => {
    e.preventDefault();
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }
    const userStatus = status?.status === "blocked" ? "unblocked" : "blocked";
    if (
      decodedToken?.roles.some(
        (role) =>
          role.role_type === roleTypes.SUPER_ADMIN ||
          role.role_type === roleTypes.ADMIN,
      )
    ) {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

        const data = {
          status: userStatus,
          email: status.email,
        };
        const response = await axios.put(
          `${url.backendURL}admin/status`,
          data,
          config,
        );

        await getAccounts((currentPage - 1) * entriesPerPage, entriesPerPage);
        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        setAlert({
          message: response && response.data.message,
          type: "success",
          show: true,
          alertFor: "accounts",
        });
        setBlockIsSubmit(true);
        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
      } catch (err) {
        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "accounts");
        setBlockIsSubmit(true);
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    if (totalEntries === 0) return;
    setCurrentPage(pageNumber);
  };

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const [showPopover, setShowPopover] = useState(null);

  const togglePopover = (idx) => {
    if (showPopover === idx) {
      setShowPopover(null);
    } else {
      setShowPopover(idx);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (showPopover !== null) {
        setShowPopover(null);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showPopover]);

  useEffect(() => {
    if (
      decodedToken?.roles.some(
        (role) =>
          role.role_type === roleTypes.SUPER_ADMIN ||
          role.role_type === roleTypes.ADMIN,
      )
    ) {
      const getRoles = async () => {
        try {
          const config = {
            headers: {
              Authorization: `${token}`,
            },
          };
          const response = await axios.get(
            `${url.backendURL}role/all/user`,
            config,
          );
          setRoles(response.data.response.data);
        } catch (err) {
          if (alertRef?.current) {
            alertRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          if (alertRefEdit?.current) {
            alertRefEdit.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          handleError(err, "accounts");
        }
      };
      getRoles();
    }
  }, [decodedToken]);

  useEffect(() => {
    const roleOptions =
      roles.length > 0 &&
      roles.map((role) => ({
        value: role.id,
        label: role.name,
      }));
    setOptions(roleOptions);
  }, [roles]);

  useEffect(() => {
    function toggleStickyNames() {
      const isWideScreen = window.innerWidth > 650;
      const stickyHeadNames = document.querySelectorAll(".sticky-head-name");
      const stickyDataNames = document.querySelectorAll(".sticky-data-names");

      if (isWideScreen) {
        stickyHeadNames.forEach((name) =>
          name.classList.add("sticky-action-head-buttons"),
        );
        stickyDataNames.forEach((name) =>
          name.classList.add("sticky-action-buttons"),
        );
      } else {
        stickyHeadNames.forEach((name) =>
          name.classList.remove("sticky-action-head-buttons"),
        );
        stickyDataNames.forEach((name) =>
          name.classList.remove("sticky-action-buttons"),
        );
      }
    }
    toggleStickyNames();
    window.addEventListener("resize", toggleStickyNames);
    return () => window.removeEventListener("resize", toggleStickyNames);
  }, []);

  const loggedInUserIsSuperAdmin = decodedToken?.roles.some(
    (role) => role.role_type === roleTypes.SUPER_ADMIN,
  );

  return (
    <>
    <style>
        {`
          .table-user .sticky-action-buttons {
            background-color: #ffffff !important;
            border: none;
            z-index: 2;
            height: auto;
          }
          .table-user .tr-user:hover .sticky-action-buttons {
            background-color: #f5f6fa !important;
          }
          .table-user .sticky-action-head-buttons {
            background: #e2e4e9 !important;
          }
          .table-user .sticky-action-head-buttons,
          .table-user .sticky-action-buttons {
            left: 0;
            z-index: 1;
            width: 180px;
            position: absolute;
            height: inherit;
            display: flex;
            align-items: center;
            padding-left: 15px;
          }
          .table-user .th-user:nth-child(2),
          .table .td-user:nth-child(2) {
            padding-left: 190px !important;
          }
           @media (max-width: 650px) {
            .table-user .sticky-action-head-buttons,
            .table-user .sticky-action-buttons {
               position: static !important;
               width: auto !important;
            }
            .table-user .th-user:nth-child(2),
            .table .td-user:nth-child(2) {
              padding-left: 0.75rem !important;
            }
          }
        `}
      </style>
      <div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between listing-card-header">
                <div className="header-title" ref={alertRef}>
                  <h4 className="card-title">User List</h4>
                </div>
              </Card.Header>

              <div className="d-flex align-items-center justify-content-between">
                <Row className="align-items-center d-flex justify-content-between mt-2 w-100">
                  {/* Show entries section - Always visible */}
                  <Col md="12" xs="12" lg="3" className="order-1">
                    <div className="pt-4 d-flex align-items-center col-12 ms-4 listing-entries">
                      <label className="me-2">Show</label>
                      <select
                        className="form-select py-1 ps-2 pe-0 text-sm bg-white rounded-lg w-25 entries-selection"
                        value={entriesPerPage}
                        onChange={handleEntriesPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span className="ms-2">entries</span>
                    </div>
                  </Col>

                  {/* Search, Role, and Add User section */}
                  <Col md="12" xs="12" lg="9" className="order-2">
                    <div className="pt-4 d-flex ms-0 ms-md-4 justify-content-start justify-content-lg-end align-items-center">
                      <Row className="g-2 ms-4 ms-md-0 align-items-end  listing-controls-row me-0">
                        {/* Search Input */}
                        <Col
                          xs="12"
                          sm="auto"
                          md="auto"
                          lg="auto"
                          className="d-flex ps-0 me-0 me-sm-0 flex-column flex-md-row align-items-start align-items-md-center listing-search-col"
                        >
                          <label className="me-2 mb-1 mb-sm-0 text-nowrap">
                            Search:
                          </label>
                          <div
                            style={{ maxWidth: "180px", width: "100%" }}
                            className="listing-search-input-wrapper pe-sm-0 pe-3"
                          >
                            <SearchInput
                              searchQuery={searchQuery}
                              setSearchQuery={setSearchQuery}
                              loading={loading}
                              setLoading={setLoading}
                            />
                          </div>
                        </Col>

                        <Col
                          xs="12"
                          sm="auto"
                          md="auto"
                          lg="auto"
                          className="d-flex ps-0 me-0 me-sm-0 flex-column flex-md-row align-items-start align-items-md-center listing-search-col"
                        >
                          <label className="me-2 mb-1 mb-sm-0 text-nowrap">
                            Role:
                          </label>
                          <div
                            style={{ minWidth: "150px", width: "100%" }}
                            className="pe-sm-0 pe-3"
                          >
                            <Select
                              closeMenuOnScroll={(e) => {
                                try {
                                  const target = e.target;

                                  if (
                                    !target ||
                                    !target.nodeType ||
                                    typeof target.closest !== "function"
                                  ) {
                                    return true;
                                  }

                                  const isMenuScroll =
                                    target.closest('[class*="MenuList"]') ||
                                    target.closest('[class*="menu"]');

                                  return !isMenuScroll;
                                } catch (error) {
                                  return true;
                                }
                              }}
                              name="roleId"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              isClearable
                              isRtl={false}
                              isSearchable={true}
                              isLoading={searchLoading}
                              styles={{
                                menu: (provided) => ({
                                  ...provided,
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                  zIndex: 9,
                                  minWidth: "150px",
                                }),
                                control: (provided) => ({
                                  ...provided,
                                  minWidth: "150px",
                                  width: "100%",
                                  textAlign: "left",
                                  justifyContent: "flex-start",
                                }),
                                option: (provided, state) => ({
                                  ...provided,
                                  width: "100%",
                                  zIndex: 9,
                                }),
                                menuList: (provided) => ({
                                  ...provided,
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                  zIndex: 9,
                                }),
                                placeholder: (provided) => ({
                                  ...provided,
                                  textAlign: "left",
                                  zIndex: 9,
                                }),
                              }}
                              placeholder="Select a Role..."
                              value={
                                options.length > 0
                                  ? options?.find(
                                      (option) => option.label === selectRole,
                                    )
                                  : null
                              }
                              onChange={(option) => {
                                if (option?.label !== selectRole) {
                                  setSearchLoading(true);
                                }
                                setSelectRole(
                                  option && option.label !== ""
                                    ? option.label
                                    : "",
                                );
                              }}
                              options={[...(options || [])]}
                            />
                          </div>
                        </Col>

                        {/* Add User Button */}
                        {(decodedToken?.roles?.some(
                          (role) => role.role_type === roleTypes.SUPER_ADMIN,
                        ) ||
                          decodedToken?.roles?.some(
                            (role) =>
                              role.role_type === roleTypes.ADMIN &&
                              hasCreatePermission,
                          )) && (
                          <Col
                            xs="12"
                            sm="auto"
                            md="auto"
                            lg="auto"
                            className="d-flex justify-content-start justify-content-md-end align-items-center ps-0 ps-sm-4 listing-add-button-col"
                          >
                            <Link
                              to={`/dashboard/app/user-add`}
                              className="mt-1 mt-sm-0"
                            >
                              <Button
                                type="submit"
                                variant="btn btn-primary"
                                className="responsive-button"
                              >
                                Add User
                              </Button>
                            </Link>
                          </Col>
                        )}
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>

              <Card.Body className="px-0">
                {alert.alertFor === "accounts" && (
                  <Row>
                    <Col className="mt-2 mb-2 ms-4 me-4">
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                    </Col>{" "}
                  </Row>
                )}

                <div  style={{ overflowX: "auto" }}>
                  <Table
                    id="user-list-table datatable"
                    className="table-user"
                    role="grid"
                    responsive
                    data-toggle="data-table"
                    style={{ minWidth: "1200px" }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#e2e4e9" }}
                        className="tr-user"
                      >
                        <th className="sticky-head-name th-user">Name</th>
                        <th className="th-user">Email</th>
                        <th className="th-user">Roles</th>
                        <th className="th-user">Status</th>
                        <th className="th-user">Warehouses</th>
                        {(hasStatusPermission ||
                          hasDeletePermission ||
                          hasUpdatePermission ||
                          decodedToken?.roles.some(
                            (role) => role.role_type === roleTypes.SUPER_ADMIN,
                          )) && (
                          <th className="th-user">
                            Action
                          </th>
                        )}
                      </tr>
                    </thead>

                    {!loader && accounts.length > 0 ? (
                      <tbody>
                        {accounts?.map((item, idx) => (
                          <tr key={idx} className="table-hover tr-user">
                            <td
                              style={{ textWrap: "revert" }}
                              className="sticky-data-names td-user"
                            >
                              {item.name}
                            </td>
                            <td
                              style={{ textWrap: "revert" }}
                              className="td-user"
                            >
                              {item.email}
                            </td>
                            <td
                              style={{ textWrap: "revert" }}
                              className="td-user"
                            >
                              {item?.roles}
                            </td>
                            <td
                              style={{ textWrap: "revert" }}
                              className="td-user"
                            >
                              <span
                                className={`badge ${getColorByStatus(
                                  item.status,
                                )}`}
                              >
                                {item.status}
                              </span>
                            </td>
                            <td
                              style={{ textWrap: "revert" }}
                              className="td-user"
                            >
                              {item?.warehouse_names?.split(",").join(" | ")}
                            </td>

                            {(hasStatusPermission ||
                              hasDeletePermission ||
                              hasUpdatePermission ||
                              decodedToken?.roles.some(
                                (role) =>
                                  role.role_type === roleTypes.SUPER_ADMIN,
                              )) && (
                              <td
                                style={{ textWrap: "revert", height: "" }}
                                className="td-user"
                              >
                                <div className="flex align-items-center justify-content-center list-user-action">
                                  {(hasStatusPermission ||
                                    decodedToken?.roles.some(
                                      (role) =>
                                        role.role_type ===
                                        roleTypes.SUPER_ADMIN,
                                    )) && (
                                    <>
                                      {" "}
                                      <Link
                                        className={`btn btn-sm btn-icon ${
                                          item?.status === "blocked"
                                            ? "btn-success"
                                            : "btn-danger"
                                        } ${
                                          item.isAdmin === 1 &&
                                          !loggedInUserIsSuperAdmin
                                            ? "opacity-50 disabled not-allowed"
                                            : ""
                                        }`}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        onClick={(e) => {
                                          if (blockIsSubmit) {
                                            setBlockIsSubmit(false);
                                            handleAccountStatus(e, item);
                                          }
                                        }}
                                        title={
                                          item?.status === "blocked"
                                            ? "Unblock"
                                            : "Block"
                                        }
                                        data-original-title={
                                          item?.status === "blocked"
                                            ? "Unblock"
                                            : "Block"
                                        }
                                      >
                                        <span className="btn-inner">
                                          {item?.status === "blocked" ? (
                                            <CgBlock
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                              }}
                                            />
                                          ) : (
                                            <CgUnblock
                                              style={{
                                                width: "20px",
                                                height: "20px",
                                              }}
                                            />
                                          )}
                                        </span>
                                      </Link>{" "}
                                    </>
                                  )}
                                  {(hasUpdatePermission ||
                                    decodedToken?.roles.some(
                                      (role) =>
                                        role.role_type ===
                                        roleTypes.SUPER_ADMIN,
                                    )) && (
                                    <>
                                      {" "}
                                      <Link
                                        className={`btn btn-sm btn-icon btn-warning ${
                                          item.isAdmin === 1 &&
                                          !loggedInUserIsSuperAdmin
                                            ? "opacity-50 disabled not-allowed"
                                            : ""
                                        }`}
                                        data-toggle="tooltip"
                                        data-placement="top"
                                        title="Edit"
                                        data-original-title="Edit"
                                        onClick={(e) => {
                                          if (
                                            item.isAdmin === 1 &&
                                            !loggedInUserIsSuperAdmin
                                          )
                                            return;

                                          navigate(
                                            `/dashboard/app/user-edit/${item.id}`,
                                          );
                                        }}
                                      >
                                        <span className="btn-inner">
                                          <svg
                                            width="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            ></path>
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            ></path>
                                            <path
                                              d="M15.1655 4.60254L19.7315 9.16854"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            ></path>
                                          </svg>
                                        </span>
                                      </Link>{" "}
                                    </>
                                  )}
                                  {(hasDeletePermission ||
                                    decodedToken?.roles.some(
                                      (role) =>
                                        role.role_type ===
                                        roleTypes.SUPER_ADMIN,
                                    )) && (
                                    <>
                                      {" "}
                                      <OverlayTrigger
                                        trigger="click"
                                        show={showPopover === `delete-${idx}`}
                                        onToggle={() =>
                                          togglePopover(`delete-${idx}`)
                                        }
                                        placement="top"
                                        overlay={
                                          <Popover id={`popover-delete-${idx}`}>
                                            <Popover.Header as="h2">
                                              <strong>Delete</strong>{" "}
                                              <span>{`(${item.name})`}</span> ?
                                            </Popover.Header>
                                            <Popover.Body>
                                              Once deleted, this action cannot
                                              be undone.
                                              <div className="d-flex justify-content-end mt-2">
                                                <Button
                                                  size="sm"
                                                  variant="danger"
                                                  className="me-2"
                                                  onClick={(e) => {
                                                    if (
                                                      item.isAdmin === 1 &&
                                                      !loggedInUserIsSuperAdmin
                                                    )
                                                      return;

                                                    if (deleteIsSubmit) {
                                                      setDeleteIsSubmit(false);
                                                      handleDeleteAccount(e);
                                                    }
                                                  }}
                                                >
                                                  Delete
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  className="text-black bg-white"
                                                  variant="secondary"
                                                  onClick={() =>
                                                    togglePopover(
                                                      `delete-${idx}`,
                                                    )
                                                  }
                                                >
                                                  Cancel
                                                </Button>
                                              </div>
                                            </Popover.Body>
                                          </Popover>
                                        }
                                      >
                                        <Link
                                          className={`btn btn-sm btn-icon btn-danger ${
                                            item.isAdmin === 1 &&
                                            !loggedInUserIsSuperAdmin
                                              ? "opacity-50 disabled not-allowed"
                                              : ""
                                          }`}
                                          data-toggle="tooltip"
                                          data-placement="top"
                                          title="Delete"
                                          data-original-title="Delete"
                                          onClick={(e) => {
                                            if (
                                              item.isAdmin === 1 &&
                                              !loggedInUserIsSuperAdmin
                                            )
                                              return;
                                            setSelectedData({
                                              name: item?.name,
                                              email: item?.email,
                                              phoneNumber: item?.phone_number,
                                              roles: item?.roles,
                                              status: item?.status,
                                              profilePicture:
                                                item?.profile_picture,
                                            });
                                          }}
                                        >
                                          <span className="btn-inner">
                                            <svg
                                              width="20"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              stroke="currentColor"
                                            >
                                              <path
                                                d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              ></path>
                                              <path
                                                d="M20.708 6.23975H3.75"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              ></path>
                                              <path
                                                d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              ></path>
                                            </svg>
                                          </span>
                                        </Link>
                                      </OverlayTrigger>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    ) : loader ? (
                      <tbody>
                        <tr>
                          <td colSpan={5}>
                            <FadeLoaderComponent />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        <tr>
                          <td colSpan={5}>
                            <div className="d-flex w-100 justify-content-center">
                              <p>No user found</p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </Table>
                </div>

                <Row className="align-items-center mt-2">
                  <Col sm="6">
                    <div className="d-flex flex-wrap items-center ms-4">
                      <div className="">
                        Showing{" "}
                        {Math.min(
                          (currentPage - 1) * entriesPerPage + 1,
                          totalEntries,
                        )}{" "}
                        to{" "}
                        {Math.min(currentPage * entriesPerPage, totalEntries)}{" "}
                        of {totalEntries} entries
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="bd-example d-flex justify-content-center justify-content-md-end me-4">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default AccountList;
